// src/private/src/pages/app/reports/DataHealthPage.jsx
import * as React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Paper,
  Skeleton,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HelpOutline as UnknownIcon,
  TrendingDown as TrendingDownIcon,
  PlayArrow as PlayArrowIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { RouterLink } from '@src/components/core/link';
import api from '@src/api/clients';
import {
  fetchDailyPulseByClient,
  fetchClientSummaryByDate,
  triggerDailyPulseBackfill,
} from '@src/api';

dayjs.extend(relativeTime);

// --- Constants ---

const STATUS_CONFIG = {
  ok: { label: 'Healthy', color: 'success', Icon: CheckCircleIcon },
  warning: { label: 'Warning', color: 'warning', Icon: WarningIcon },
  anomaly: { label: 'Anomaly', color: 'error', Icon: TrendingDownIcon },
  missing: { label: 'Missing', color: 'error', Icon: ErrorIcon },
  unknown: { label: 'Unknown', color: 'default', Icon: UnknownIcon },
};

const TASK_STATUS_COLOR = {
  succeeded: 'success',
  failed: 'error',
  running: 'info',
  missing: 'default',
  present: 'success',
};

const MAPPING_TYPE_FROM_TASK = {
  email_mapping: 'Email',
  process_email_mapping: 'Email',
  sftp_mappings: 'SFTP',
  process_sftp_mappings: 'SFTP',
  api_mappings: 'API',
  process_api_mappings: 'API',
  google_sheet_mappings: 'GSheet',
  process_google_sheet_mappings: 'GSheet',
};

const VOLUME_DROP_THRESHOLD = 0.5; // flag if yesterday < 50% of 7d avg
const AUTO_REFRESH_MS = 5 * 60 * 1000; // 5 minutes

// --- Helper functions ---

const isTaskFresh = (task) => {
  if (!task || task.status === 'missing') return false;
  if (task.name === 'backend_data') {
    if (!task.max_created_date) return false;
    const maxDate = dayjs(task.max_created_date);
    if (!maxDate.isValid()) return Boolean(task.fresh);
    return dayjs().startOf('day').diff(maxDate.startOf('day'), 'day') <= 1;
  }
  if (task.finished_at) {
    const finished = dayjs(task.finished_at);
    if (finished.isValid()) return dayjs().diff(finished, 'hour', true) <= 24;
  }
  return Boolean(task.fresh);
};

const isTaskHealthy = (task) => {
  if (!task || task.status === 'missing') return false;
  if (task.name === 'backend_data') return isTaskFresh(task);
  return String(task.status).toLowerCase() === 'succeeded' && isTaskFresh(task);
};

const getBackendDataDate = (health) => {
  const tasks = Array.isArray(health?.tasks) ? health.tasks : [];
  const bd = tasks.find((t) => t.name === 'backend_data');
  return bd?.max_created_date || null;
};

const getMappingTypes = (health) => {
  const tasks = Array.isArray(health?.tasks) ? health.tasks : [];
  const types = new Set();
  tasks.forEach((t) => {
    const name = (t.name || '').replace(/\s/g, '_');
    Object.keys(MAPPING_TYPE_FROM_TASK).forEach((key) => {
      if (name.includes(key) || name === key) types.add(MAPPING_TYPE_FROM_TASK[key]);
    });
  });
  return [...types];
};

const deriveClientHealth = (row, anomalies) => {
  const health = row?.ingestion_health;
  const clientId = row?.client_id;
  const anom = anomalies?.[clientId];

  if (!health) return { status: 'unknown', issues: [], stalestAge: null, daysStalePipeline: 0 };
  if (!health.has_mapping) return { status: 'missing', issues: ['No data mappings configured'], stalestAge: null, daysStalePipeline: 0 };

  const tasks = Array.isArray(health.tasks) ? health.tasks : [];
  const issues = [];
  let stalestAge = null;

  for (const task of tasks) {
    if (!isTaskHealthy(task)) {
      const label = task?.name || 'Unknown';
      const statusText = task?.status === 'missing' ? 'never ran' : (task?.status || 'unknown');
      issues.push(`${label}: ${statusText}`);
    }
    const finishedAt = task?.finished_at ? dayjs(task.finished_at) : null;
    const maxDate = task?.max_created_date ? dayjs(task.max_created_date) : null;
    const ref = finishedAt?.isValid() ? finishedAt : maxDate?.isValid() ? maxDate : null;
    if (ref) {
      const age = dayjs().diff(ref, 'minute');
      if (stalestAge === null || age > stalestAge) stalestAge = age;
    }
  }

  const daysStalePipeline = stalestAge !== null ? Math.max(0, Math.floor(stalestAge / 1440) - 0) : 0;

  // Backend-data feed health — the "why" behind a stale feed (no new report /
  // arrived empty / errored), read from the lead-flow loop so it matches the
  // page's alerts and Slack.
  if (health.feed_alert?.reason) {
    issues.push(health.feed_alert.reason);
  }

  // Check for anomalies
  if (anom?.leadDrop || anom?.spendDrop) {
    const anomIssues = [];
    if (anom.leadDrop) anomIssues.push(`Leads dropped ${Math.round((1 - anom.leadRatio) * 100)}% vs 7d avg`);
    if (anom.spendDrop) anomIssues.push(`Spend dropped ${Math.round((1 - anom.spendRatio) * 100)}% vs 7d avg`);
    issues.push(...anomIssues);
  }

  let status = 'ok';
  if (anom?.leadDrop || anom?.spendDrop) status = 'anomaly';
  else if (issues.length > 0) status = 'warning';

  return { status, issues, stalestAge, daysStalePipeline };
};

const formatAge = (minutes) => {
  if (minutes === null || minutes === undefined) return '-';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}d`;
};

const formatDate = (d) => d.toISOString().split('T')[0];

const buildAnomalyLookup = (dailyRows) => {
  // Group by client_id → array of { date, total_spend_sum, total_leads_sum }
  const byClient = {};
  (Array.isArray(dailyRows) ? dailyRows : []).forEach((row) => {
    const id = row.client_id;
    if (!id) return;
    if (!byClient[id]) byClient[id] = [];
    byClient[id].push({
      date: row.date,
      spend: Number(row.total_spend_sum) || 0,
      leads: Number(row.total_leads_sum) || 0,
    });
  });

  const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
  const lookup = {};

  Object.entries(byClient).forEach(([clientId, days]) => {
    days.sort((a, b) => (a.date > b.date ? 1 : -1));
    const yesterdayRow = days.find((d) => d.date === yesterday);
    // Use all days except yesterday for the baseline
    const baselineDays = days.filter((d) => d.date !== yesterday);

    if (!yesterdayRow || baselineDays.length < 2) {
      lookup[clientId] = { leadDrop: false, spendDrop: false, leadRatio: 1, spendRatio: 1, yesterdayLeads: null, avgLeads: null, yesterdaySpend: null, avgSpend: null };
      return;
    }

    const avgLeads = baselineDays.reduce((s, d) => s + d.leads, 0) / baselineDays.length;
    const avgSpend = baselineDays.reduce((s, d) => s + d.spend, 0) / baselineDays.length;

    const leadRatio = avgLeads > 0 ? yesterdayRow.leads / avgLeads : 1;
    const spendRatio = avgSpend > 0 ? yesterdayRow.spend / avgSpend : 1;

    lookup[clientId] = {
      leadDrop: avgLeads >= 1 && leadRatio < VOLUME_DROP_THRESHOLD,
      spendDrop: avgSpend >= 5 && spendRatio < VOLUME_DROP_THRESHOLD,
      leadRatio,
      spendRatio,
      yesterdayLeads: yesterdayRow.leads,
      avgLeads: Math.round(avgLeads * 10) / 10,
      yesterdaySpend: Math.round(yesterdayRow.spend),
      avgSpend: Math.round(avgSpend),
    };
  });

  return lookup;
};

// --- Alerts Panel ---

function AlertsPanel() {
  const [activeTab, setActiveTab] = React.useState('live'); // 'live' | 'thresholds' | 'history'
  const [alertResults, setAlertResults] = React.useState(null);
  const [thresholds, setThresholds] = React.useState(null);
  const [history, setHistory] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [editingClient, setEditingClient] = React.useState(null);
  const [editValues, setEditValues] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  const fetchAlerts = React.useCallback(() => {
    setLoading(true);
    api.request({ url: 'dashboard/alerts/evaluate-all/', method: 'GET' })
      .then((resp) => setAlertResults(resp?.data || null))
      .catch((err) => console.error('Alert eval error', err))
      .finally(() => setLoading(false));
  }, []);

  const fetchThresholds = React.useCallback(() => {
    setLoading(true);
    api.request({ url: 'dashboard/alerts/thresholds/', method: 'GET' })
      .then((resp) => setThresholds(resp?.data || null))
      .catch((err) => console.error('Threshold fetch error', err))
      .finally(() => setLoading(false));
  }, []);

  const fetchHistory = React.useCallback(() => {
    setLoading(true);
    api.request({ url: 'dashboard/alerts/history/', method: 'GET', params: { limit: 30 } })
      .then((resp) => setHistory(resp?.data || null))
      .catch((err) => console.error('History fetch error', err))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (activeTab === 'live' && !alertResults) fetchAlerts();
    if (activeTab === 'thresholds' && !thresholds) fetchThresholds();
    if (activeTab === 'history' && !history) fetchHistory();
  }, [activeTab, alertResults, thresholds, history, fetchAlerts, fetchThresholds, fetchHistory]);

  const startEditing = (client) => {
    setEditingClient(client.client_id);
    setEditValues({
      alert_lead_sample_size: client.alert_lead_sample_size,
      alert_out_of_area_threshold: client.alert_out_of_area_threshold,
      alert_success_post_threshold: client.alert_success_post_threshold,
      alert_webhook_freshness_minutes: client.alert_webhook_freshness_minutes,
    });
  };

  const saveThreshold = () => {
    setSaving(true);
    api.request({
      url: 'dashboard/alerts/thresholds/',
      method: 'PATCH',
      payload: { client_id: editingClient, ...editValues },
    })
      .then(() => {
        setEditingClient(null);
        setThresholds(null); // force refetch
        fetchThresholds();
      })
      .catch((err) => console.error('Save error', err))
      .finally(() => setSaving(false));
  };

  const triggeredClients = alertResults?.clients?.filter((c) => c.any_triggered) || [];

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="Alerts"
        subheader="Live alert evaluation, thresholds, and history"
        action={
          <Stack direction="row" spacing={0.5}>
            {['live', 'thresholds', 'history'].map((tab) => (
              <Button
                key={tab}
                size="small"
                variant={activeTab === tab ? 'contained' : 'outlined'}
                onClick={() => setActiveTab(tab)}
                sx={{ textTransform: 'capitalize', minWidth: 80 }}
              >
                {tab === 'live' ? `Live (${alertResults?.triggered_count ?? '?'})` : tab}
              </Button>
            ))}
          </Stack>
        }
      />
      <Divider />
      <CardContent>
        {loading && <CircularProgress size={20} sx={{ display: 'block', mx: 'auto', my: 2 }} />}

        {/* Live tab */}
        {activeTab === 'live' && !loading && alertResults && (
          <Box>
            <Stack direction="row" spacing={2} mb={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {alertResults.total_clients} clients evaluated, <strong>{alertResults.triggered_count}</strong> with active alerts
              </Typography>
              <Button size="small" variant="text" onClick={fetchAlerts} startIcon={<Refresh sx={{ fontSize: 16 }} />}>Re-evaluate</Button>
            </Stack>
            {triggeredClients.length === 0 ? (
              <Typography variant="body2" color="success.main" py={2}>All clients are healthy — no alerts triggered.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Triggered Alerts</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {triggeredClients.map((c) => (
                    <TableRow key={c.client_id} sx={(theme) => ({ backgroundColor: alpha(theme.palette.error.light, 0.06) })}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight={600}>{c.client_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {c.triggered_alerts.map((a) => (
                            <Chip key={a} label={a.replace(/_/g, ' ')} size="small" color="error" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {/* The loop's own detail text — same wording the Slack
                            alert uses, so the two surfaces can never disagree. */}
                        {(c.details || []).map((detail, i) => (
                          <Typography key={i} variant="caption" display="block">{detail}</Typography>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {/* Thresholds tab */}
        {activeTab === 'thresholds' && !loading && thresholds && (
          <Box>
            <GlobalThresholds />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Sample Size</TableCell>
                  <TableCell>OOA Threshold</TableCell>
                  <TableCell>Success Post %</TableCell>
                  <TableCell>Webhook Fresh (min)</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(thresholds.clients || []).map((c) => {
                  const isEditing = editingClient === c.client_id;
                  return (
                    <TableRow key={c.client_id}>
                      <TableCell><Typography variant="body2" fontWeight={500}>{c.client_name}</Typography></TableCell>
                      {isEditing ? (
                        <React.Fragment>
                          <TableCell><TextField size="small" type="number" value={editValues.alert_lead_sample_size} onChange={(e) => setEditValues((v) => ({ ...v, alert_lead_sample_size: e.target.value }))} sx={{ width: 80 }} /></TableCell>
                          <TableCell><TextField size="small" type="number" value={editValues.alert_out_of_area_threshold} onChange={(e) => setEditValues((v) => ({ ...v, alert_out_of_area_threshold: e.target.value }))} sx={{ width: 80 }} /></TableCell>
                          <TableCell><TextField size="small" type="number" value={editValues.alert_success_post_threshold} onChange={(e) => setEditValues((v) => ({ ...v, alert_success_post_threshold: e.target.value }))} sx={{ width: 80 }} /></TableCell>
                          <TableCell><TextField size="small" type="number" value={editValues.alert_webhook_freshness_minutes} onChange={(e) => setEditValues((v) => ({ ...v, alert_webhook_freshness_minutes: e.target.value }))} sx={{ width: 80 }} /></TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Button size="small" variant="contained" onClick={saveThreshold} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
                              <Button size="small" variant="text" onClick={() => setEditingClient(null)}>Cancel</Button>
                            </Stack>
                          </TableCell>
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <TableCell>{c.alert_lead_sample_size}</TableCell>
                          <TableCell>{c.alert_out_of_area_threshold}%</TableCell>
                          <TableCell>{c.alert_success_post_threshold}%</TableCell>
                          <TableCell>{c.alert_webhook_freshness_minutes}m</TableCell>
                          <TableCell>
                            <Button size="small" variant="text" onClick={() => startEditing(c)}>Edit</Button>
                          </TableCell>
                        </React.Fragment>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* History tab */}
        {activeTab === 'history' && !loading && history && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {history.count} recent alert task runs
            </Typography>
            {history.entries?.length === 0 ? (
              <Typography variant="body2" color="text.secondary" py={2}>No alert task history found.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Task</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(history.entries || []).map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Typography variant="body2">{entry.name?.replace(/_/g, ' ')}</Typography>
                        {entry.client_name && <Typography variant="caption" color="text.secondary">{entry.client_name}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.status}
                          size="small"
                          color={entry.status === 'succeeded' ? 'success' : entry.status === 'failed' ? 'error' : 'default'}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{entry.created ? dayjs(entry.created).fromNow() : '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{entry.duration_seconds ? `${Math.round(entry.duration_seconds)}s` : '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        {entry.messages?.map((msg, i) => (
                          <Typography key={i} variant="caption" display="block" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {msg}
                          </Typography>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// --- Editable loop thresholds ---
// The numbers the loops actually use to decide what's an alert. Editing one
// changes detection in BOTH Slack and this page (they read the same loop).
const GLOBAL_THRESHOLD_FIELDS = [
  { key: 'volume_drop_ratio', label: 'Volume drop (× baseline)', step: '0.05' },
  { key: 'min_baseline_leads', label: 'Min baseline leads', step: '1' },
  { key: 'duplicate_spike_points', label: 'Duplicate spike (pts)', step: '1' },
  { key: 'out_of_area_pct', label: 'Out-of-area (%)', step: '1' },
  { key: 'reject_rate_pct', label: 'Reject rate (%)', step: '1' },
  { key: 'budget_overspend_pct', label: 'Budget overspend (%)', step: '5' },
  { key: 'budget_underspend_pct', label: 'Budget underspend (%)', step: '5' },
];

function GlobalThresholds() {
  const [values, setValues] = React.useState(null);
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    api.request({ url: 'dashboard/alerts/global-thresholds/', method: 'GET' })
      .then((resp) => { if (active) setValues(resp?.data?.thresholds || null); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const save = () => {
    setSaving(true);
    api.request({ url: 'dashboard/alerts/global-thresholds/', method: 'PATCH', payload: values })
      .then((resp) => { setValues(resp?.data?.thresholds || values); setDirty(false); })
      .catch((err) => console.error('Save thresholds error', err))
      .finally(() => setSaving(false));
  };

  if (!values) return null;

  return (
    <Box mb={2} p={1.5} sx={(theme) => ({ backgroundColor: alpha(theme.palette.info.light, 0.08), borderRadius: 1 })}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" fontWeight={600}>
          Loop thresholds — what triggers an alert (applies to all clients; drives Slack + this page)
        </Typography>
        <Button
          size="small"
          variant="contained"
          disabled={!dirty || saving}
          onClick={save}
          startIcon={saving ? <CircularProgress size={12} /> : null}
        >
          Save
        </Button>
      </Stack>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {GLOBAL_THRESHOLD_FIELDS.map((f) => (
          <TextField
            key={f.key}
            label={f.label}
            type="number"
            size="small"
            value={values[f.key] ?? ''}
            onChange={(e) => { setValues((v) => ({ ...v, [f.key]: e.target.value })); setDirty(true); }}
            inputProps={{ step: f.step, min: 0 }}
            sx={{ width: 170 }}
          />
        ))}
      </Stack>
    </Box>
  );
}

// --- System-health banner ---
// System-scoped anomalies from the system-health loop (no client FK) — pipeline
// /job problems that aren't about one client. Read straight from the loop so it
// matches Slack. Renders nothing when all clear.
function SystemHealthBanner() {
  const [items, setItems] = React.useState([]);

  React.useEffect(() => {
    let active = true;
    api.request({ url: 'dashboard/alerts/system-status/', method: 'GET' })
      .then((resp) => { if (active) setItems(resp?.data?.anomalies || []); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!items.length) return null;

  const sevColor = (s) => (s === 'critical' || s === 'high' ? 'error' : s === 'medium' ? 'warning' : 'info');
  const worst = items.some((i) => i.severity === 'critical' || i.severity === 'high') ? 'error' : 'warning';

  return (
    <Alert severity={worst} icon={<WarningIcon fontSize="inherit" />} sx={{ mb: 2 }}>
      <AlertTitle>System health — {items.length} active issue{items.length !== 1 ? 's' : ''}</AlertTitle>
      <Stack spacing={0.5}>
        {items.map((it, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              label={String(it.severity)}
              size="small"
              color={sevColor(it.severity)}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
            />
            <Typography variant="body2"><strong>{it.title}</strong></Typography>
          </Stack>
        ))}
      </Stack>
    </Alert>
  );
}

// --- Main Component ---

export default function DataHealthPage() {
  const [clients, setClients] = React.useState([]);
  const [anomalies, setAnomalies] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [orderBy, setOrderBy] = React.useState('staleness');
  const [orderDir, setOrderDir] = React.useState('desc');
  const [clientSlugLookup, setClientSlugLookup] = React.useState({});
  const [parentLookup, setParentLookup] = React.useState({});
  const [statusLookup, setStatusLookup] = React.useState({});
  const [autoRefresh, setAutoRefresh] = React.useState(false);
  const [backfillLoading, setBackfillLoading] = React.useState({});
  const [expandedParents, setExpandedParents] = React.useState(new Set());
  const [groupByParent, setGroupByParent] = React.useState(false);
  const [showInactive, setShowInactive] = React.useState(false);
  const [lastRefreshed, setLastRefreshed] = React.useState(null);

  const fetchData = React.useCallback(() => {
    setIsLoading(true);

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    Promise.all([
      // Current month client data with ingestion health.
      // Default to active clients only — paused/off clients (0 leads, $0 spend,
      // 100+ days stale) are noise here, not data-health problems.
      fetchDailyPulseByClient({
        date_after: formatDate(monthStart),
        date_before: formatDate(monthEnd),
        page_size: 200,
        ...(showInactive ? {} : { active: true }),
      }),
      // Last 7 days per-client per-day data for anomaly detection
      fetchClientSummaryByDate({
        date_after: formatDate(sevenDaysAgo),
        date_before: formatDate(yesterday),
        page_size: 2000,
      }),
      // Client metadata
      api.request({
        url: 'dashboard/clients/',
        method: 'GET',
        params: { fields: 'id,slug,name,parent_company,parent_company_name,status', page_size: 500 },
      }),
    ])
      .then(([pulseResp, dailyResp, clientsResp]) => {
        const rows = Array.isArray(pulseResp?.data?.results)
          ? pulseResp.data.results
          : Array.isArray(pulseResp?.data) ? pulseResp.data : [];

        const dailyRows = Array.isArray(dailyResp?.data?.results)
          ? dailyResp.data.results
          : Array.isArray(dailyResp?.data) ? dailyResp.data : [];

        const clientList = Array.isArray(clientsResp?.data?.results)
          ? clientsResp.data.results
          : Array.isArray(clientsResp?.data) ? clientsResp.data : [];

        const slugMap = {};
        const parentMap = {};
        const statusMap = {};
        clientList.forEach((c) => {
          if (c?.id) {
            if (c.slug) slugMap[String(c.id)] = c.slug;
            if (c.parent_company_name) parentMap[String(c.id)] = c.parent_company_name;
            if (c.status) statusMap[String(c.id)] = c.status;
          }
        });
        setClientSlugLookup(slugMap);
        setParentLookup(parentMap);
        setStatusLookup(statusMap);

        const anomalyLookup = buildAnomalyLookup(dailyRows);
        setAnomalies(anomalyLookup);

        const enriched = rows
          .filter((r) => r?.client__name)
          .map((row) => ({
            ...row,
            _health: deriveClientHealth(row, anomalyLookup),
            _backendDate: getBackendDataDate(row.ingestion_health),
            _mappings: getMappingTypes(row.ingestion_health),
            _anomaly: anomalyLookup[row.client_id] || null,
          }));

        setClients(enriched);
        setLastRefreshed(new Date());
      })
      .catch((err) => {
        console.error('DataHealthPage fetch error', err);
        setClients([]);
      })
      .finally(() => setIsLoading(false));
  }, [showInactive]);

  React.useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh
  React.useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrderDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrderDir(field === 'client' ? 'asc' : 'desc');
    }
  };

  const handleBackfill = React.useCallback((clientId) => {
    setBackfillLoading((prev) => ({ ...prev, [clientId]: true }));
    const client = clients.find((c) => c.client_id === clientId);
    const staleDays = client?._health?.daysStalePipeline || 0;
    const backfillDays = Math.max(3, Math.min(staleDays + 1, 14));
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - backfillDays);
    triggerDailyPulseBackfill({
      client_id: clientId,
      start_date: formatDate(start),
      end_date: formatDate(now),
    })
      .then(() => {
        setBackfillLoading((prev) => ({ ...prev, [clientId]: 'done' }));
        setTimeout(() => setBackfillLoading((prev) => ({ ...prev, [clientId]: false })), 3000);
      })
      .catch(() => {
        setBackfillLoading((prev) => ({ ...prev, [clientId]: 'error' }));
        setTimeout(() => setBackfillLoading((prev) => ({ ...prev, [clientId]: false })), 3000);
      });
  }, []);

  const filteredClients = React.useMemo(() => {
    let list = clients;
    if (statusFilter !== 'all') {
      list = list.filter((c) => c._health.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((c) => {
        const name = (c.client__name || '').toLowerCase();
        const parent = (parentLookup[String(c.client_id)] || '').toLowerCase();
        return name.includes(q) || parent.includes(q);
      });
    }
    const dir = orderDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (orderBy === 'client') return dir * (a.client__name || '').localeCompare(b.client__name || '');
      if (orderBy === 'status') {
        const order = { anomaly: 0, missing: 1, warning: 2, unknown: 3, ok: 4 };
        return dir * ((order[a._health.status] ?? 3) - (order[b._health.status] ?? 3));
      }
      if (orderBy === 'staleness') return dir * ((a._health.stalestAge ?? -1) - (b._health.stalestAge ?? -1));
      if (orderBy === 'leads') {
        const aRatio = a._anomaly?.leadRatio ?? 1;
        const bRatio = b._anomaly?.leadRatio ?? 1;
        return dir * (aRatio - bRatio);
      }
      if (orderBy === 'spend') {
        const aRatio = a._anomaly?.spendRatio ?? 1;
        const bRatio = b._anomaly?.spendRatio ?? 1;
        return dir * (aRatio - bRatio);
      }
      if (orderBy === 'issues') return dir * (a._health.issues.length - b._health.issues.length);
      return 0;
    });
  }, [clients, statusFilter, searchQuery, orderBy, orderDir, parentLookup]);

  const statusCounts = React.useMemo(() => {
    const counts = { all: 0, ok: 0, warning: 0, missing: 0, anomaly: 0 };
    clients.forEach((c) => { counts.all++; const s = c._health.status; if (s in counts) counts[s]++; });
    return counts;
  }, [clients]);

  // Group by parent company
  const groupedClients = React.useMemo(() => {
    if (!groupByParent) return null;
    const groups = {};
    filteredClients.forEach((c) => {
      const parent = parentLookup[String(c.client_id)] || 'No Parent Company';
      if (!groups[parent]) groups[parent] = [];
      groups[parent].push(c);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [groupByParent, filteredClients, parentLookup]);

  const buildClientPath = (clientId) => {
    const slug = clientSlugLookup[String(clientId)];
    return `/clients/${slug || clientId}/dashboard`;
  };

  const renderSortHeader = (label, field, props = {}) => (
    <TableCell {...props}>
      <TableSortLabel active={orderBy === field} direction={orderBy === field ? orderDir : 'asc'} onClick={() => handleSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const renderAnomalyCell = (value, avg, ratio, isDrop, label) => {
    if (value === null || value === undefined) return <Typography variant="body2" color="text.secondary">-</Typography>;
    if (!isDrop) {
      return <Typography variant="body2">{label === 'spend' ? `$${value.toLocaleString()}` : value}</Typography>;
    }
    const pctDrop = Math.round((1 - ratio) * 100);
    return (
      <Tooltip arrow title={`Yesterday: ${label === 'spend' ? '$' : ''}${value.toLocaleString()} vs 7d avg: ${label === 'spend' ? '$' : ''}${avg.toLocaleString()}`}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
          <Typography variant="body2" color="error.main" fontWeight={600}>
            {label === 'spend' ? `$${value.toLocaleString()}` : value}
          </Typography>
          <Typography variant="caption" color="error.main">-{pctDrop}%</Typography>
        </Stack>
      </Tooltip>
    );
  };

  const renderRow = (row) => {
    const h = row._health;
    const cfg = STATUS_CONFIG[h.status] || STATUS_CONFIG.unknown;
    const StatusIcon = cfg.Icon;
    const tasks = Array.isArray(row.ingestion_health?.tasks) ? row.ingestion_health.tasks : [];
    const anom = row._anomaly;
    const bfState = backfillLoading[row.client_id];
    const parent = parentLookup[String(row.client_id)];

    return (
      <TableRow
        key={row.uuid || row.client_id}
        sx={(theme) => ({
          ...(h.status === 'anomaly' && { backgroundColor: alpha(theme.palette.error.light, 0.08) }),
          ...(h.status === 'warning' && { backgroundColor: alpha(theme.palette.warning.light, 0.06) }),
          ...(h.status === 'missing' && { backgroundColor: alpha(theme.palette.error.light, 0.04) }),
        })}
      >
        {/* Client */}
        <TableCell>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography component={RouterLink} href={buildClientPath(row.client_id)} variant="subtitle2" sx={{ textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
              {row.client__name}
            </Typography>
            {statusLookup[String(row.client_id)] === 'active_new' ? (
              <Chip label="NEW" size="small" color="info" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} title="Newly launched — monitored more closely" />
            ) : null}
          </Stack>
          {!groupByParent && parent ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.1 }}>{parent}</Typography>
          ) : null}
        </TableCell>

        {/* Status */}
        <TableCell>
          <Chip icon={<StatusIcon sx={{ fontSize: 16 }} />} label={cfg.label} color={cfg.color} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.75rem' }} />
        </TableCell>

        {/* Last Data */}
        <TableCell>
          {row._backendDate ? (
            <Tooltip arrow title={`Backend data last seen: ${row._backendDate}`}>
              <Typography variant="body2" color={dayjs().diff(dayjs(row._backendDate), 'day') > 1 ? 'error.main' : 'text.primary'} fontWeight={dayjs().diff(dayjs(row._backendDate), 'day') > 1 ? 600 : 400}>
                {dayjs(row._backendDate).format('MM/DD')}
              </Typography>
            </Tooltip>
          ) : <Typography variant="body2" color="text.secondary">-</Typography>}
        </TableCell>

        {/* Days Stale */}
        <TableCell>
          <Typography variant="body2" color={h.daysStalePipeline > 1 ? 'error.main' : h.daysStalePipeline === 1 ? 'warning.main' : 'text.primary'} fontWeight={h.daysStalePipeline > 0 ? 600 : 400}>
            {h.stalestAge !== null ? (h.daysStalePipeline > 0 ? `${h.daysStalePipeline}d` : formatAge(h.stalestAge)) : '-'}
          </Typography>
        </TableCell>

        {/* Leads (anomaly) */}
        <TableCell>
          {renderAnomalyCell(anom?.yesterdayLeads, anom?.avgLeads, anom?.leadRatio, anom?.leadDrop, 'leads')}
        </TableCell>

        {/* Spend (anomaly) */}
        <TableCell>
          {renderAnomalyCell(anom?.yesterdaySpend, anom?.avgSpend, anom?.spendRatio, anom?.spendDrop, 'spend')}
        </TableCell>

        {/* Issues */}
        <TableCell>
          {h.issues.length > 0 ? (
            <Tooltip arrow title={h.issues.join('\n')} slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}>
              <Typography variant="body2" color="warning.main" fontWeight={600} sx={{ cursor: 'default' }}>{h.issues.length}</Typography>
            </Tooltip>
          ) : <Typography variant="body2" color="success.main">0</Typography>}
        </TableCell>

        {/* Mappings */}
        <TableCell>
          <Stack direction="row" spacing={0.25}>
            {row._mappings.map((m) => (
              <Chip key={m} label={m} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
            ))}
            {row._mappings.length === 0 && <Typography variant="caption" color="text.secondary">-</Typography>}
          </Stack>
        </TableCell>

        {/* Tasks */}
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {tasks.map((task, idx) => {
              const healthy = isTaskHealthy(task);
              const chipColor = healthy ? 'success' : task.status === 'missing' ? 'default' : TASK_STATUS_COLOR[task.status] || 'warning';
              const tooltipLines = [`Status: ${task.status || 'unknown'}`];
              if (task.runtime_seconds) tooltipLines.push(`Runtime: ${Math.round(task.runtime_seconds)}s`);
              if (task.finished_at) tooltipLines.push(`Finished: ${dayjs(task.finished_at).fromNow()}`);
              if (task.max_created_date) tooltipLines.push(`Latest data: ${task.max_created_date}`);
              if (!isTaskFresh(task)) tooltipLines.push('Stale (>24h)');
              return (
                <Tooltip key={`${task.name}-${idx}`} arrow title={tooltipLines.join('\n')} slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}>
                  <Chip label={task.name?.replace(/_/g, ' ').replace('process ', '')} size="small" color={chipColor} variant={healthy ? 'outlined' : 'filled'} sx={{ fontSize: '0.65rem', height: 20 }} />
                </Tooltip>
              );
            })}
          </Stack>
        </TableCell>

        {/* Actions */}
        <TableCell>
          <Tooltip title={`Backfill last ${Math.max(3, Math.min((h.daysStalePipeline || 0) + 1, 14))} days`}>
            <span>
              <IconButton
                size="small"
                color={bfState === 'done' ? 'success' : bfState === 'error' ? 'error' : 'primary'}
                disabled={bfState === true}
                onClick={() => handleBackfill(row.client_id)}
              >
                {bfState === true ? <CircularProgress size={16} /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  const colCount = 10;

  const renderParentGroup = ([parentName, groupRows]) => {
    const isExpanded = expandedParents.has(parentName);
    const worstStatus = groupRows.reduce((worst, c) => {
      const order = { anomaly: 0, missing: 1, warning: 2, unknown: 3, ok: 4 };
      return (order[c._health.status] ?? 3) < (order[worst] ?? 3) ? c._health.status : worst;
    }, 'ok');
    const cfg = STATUS_CONFIG[worstStatus] || STATUS_CONFIG.ok;

    return (
      <React.Fragment key={parentName}>
        <TableRow
          sx={(theme) => ({ backgroundColor: alpha(theme.palette.primary.main, 0.04), cursor: 'pointer' })}
          onClick={() => setExpandedParents((prev) => {
            const next = new Set(prev);
            if (next.has(parentName)) next.delete(parentName); else next.add(parentName);
            return next;
          })}
        >
          <TableCell colSpan={colCount}>
            <Stack direction="row" spacing={1} alignItems="center">
              {isExpanded ? <KeyboardArrowDown sx={{ fontSize: 18 }} /> : <KeyboardArrowRight sx={{ fontSize: 18 }} />}
              <Typography variant="subtitle2" fontWeight={700}>{parentName}</Typography>
              <Chip label={`${groupRows.length} clients`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              <Chip label={cfg.label} size="small" color={cfg.color} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            </Stack>
          </TableCell>
        </TableRow>
        {isExpanded && groupRows.map(renderRow)}
      </React.Fragment>
    );
  };

  return (
    <Box p={3}>
      <SystemHealthBanner />
      <Card>
        <CardHeader
          title="Data Health Monitor"
          subheader="Pipeline status, data freshness, and anomaly detection across all clients"
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
                label={<Typography variant="caption">Show inactive</Typography>}
                sx={{ mr: 0 }}
              />
              <FormControlLabel
                control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
                label={<Typography variant="caption">Auto-refresh</Typography>}
                sx={{ mr: 0 }}
              />
              <Button variant="outlined" size="small" startIcon={isLoading ? <CircularProgress size={14} /> : <Refresh fontSize="small" />} onClick={fetchData} disabled={isLoading}>
                Refresh
              </Button>
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          {/* Summary chips */}
          <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap" alignItems="center">
            {[
              { key: 'all', label: `All (${statusCounts.all})` },
              { key: 'anomaly', label: `Anomalies (${statusCounts.anomaly})`, color: 'error' },
              { key: 'warning', label: `Warnings (${statusCounts.warning})`, color: 'warning' },
              { key: 'missing', label: `Missing (${statusCounts.missing})`, color: 'error' },
              { key: 'ok', label: `Healthy (${statusCounts.ok})`, color: 'success' },
            ].map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                color={statusFilter === chip.key ? (chip.color || 'primary') : 'default'}
                variant={statusFilter === chip.key ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter(chip.key)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
            {lastRefreshed && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                Updated {dayjs(lastRefreshed).format('h:mm A')}
              </Typography>
            )}
          </Stack>

          {/* Controls */}
          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
              }}
            />
            <FormControlLabel
              control={<Switch size="small" checked={groupByParent} onChange={(e) => setGroupByParent(e.target.checked)} />}
              label={<Typography variant="caption">Group by parent</Typography>}
            />
            <Typography variant="caption" color="text.secondary">
              {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>

          <TableContainer component={Paper} sx={{ maxHeight: '68vh', borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {renderSortHeader('Client', 'client', { sx: { minWidth: 160 } })}
                  {renderSortHeader('Status', 'status', { sx: { minWidth: 90 } })}
                  <TableCell sx={{ minWidth: 70 }}>Last Data</TableCell>
                  {renderSortHeader('Stale', 'staleness', { sx: { minWidth: 60 } })}
                  {renderSortHeader('Leads (yd)', 'leads', { sx: { minWidth: 90 } })}
                  {renderSortHeader('Spend (yd)', 'spend', { sx: { minWidth: 100 } })}
                  {renderSortHeader('Issues', 'issues', { sx: { minWidth: 55 } })}
                  <TableCell sx={{ minWidth: 100 }}>Mappings</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>Tasks</TableCell>
                  <TableCell sx={{ minWidth: 50 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={`skel-${i}`}>
                      {Array.from({ length: colCount }).map((_, j) => (
                        <TableCell key={j}><Skeleton width={j === 0 ? 130 : j === 8 ? 200 : 60} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center">
                      <Typography variant="body2" color="text.secondary" py={4}>
                        {searchQuery ? 'No clients match your search.' : 'No clients found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : groupByParent && groupedClients ? (
                  groupedClients.map(renderParentGroup)
                ) : (
                  filteredClients.map(renderRow)
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Live Alerts Panel */}
          <AlertsPanel />
        </CardContent>
      </Card>
    </Box>
  );
}
